import { PackageIcon } from "@phosphor-icons/react/Package";
import { Icon } from "../Icons/Icon";
import { T } from "../Typography/T";
import { Tooltip } from "../Feedback/Tooltip";
import { CountryFlag } from "../Display/CountryFlag";

/**
 * One shipment's pickup, delivery and freight, as three flag/icon-led lines.
 *
 * Lives here rather than in an app so Hubble and the carrier portal show a
 * shipment the same way — a carrier reading their own shipment list and an
 * operator reading the same row should not be looking at two different designs.
 *
 * Takes plain strings only. The app-side adapters (Hubble's Shipment overload,
 * the carrier portal's summary row) do their own flattening.
 */
export interface ShipmentDetailsCellProps {
  pickupName?: string | undefined;
  pickupLabel: string;
  pickupCountry?: string | undefined;
  deliveryName?: string | undefined;
  deliveryLabel: string;
  deliveryCountry?: string | undefined;
  commodity: string;
  summary: string;
}

// Pickup/delivery share the same two-line shape:
// the business name is the prominent line, the city/state sits beneath it
// muted. Falls back to a single location line when no business name is known
// (some flattened row types don't carry it).
function LocationLine_({
  countryCode,
  businessName,
  label,
}: {
  countryCode?: string | undefined;
  businessName?: string | undefined;
  label: string;
}) {
  return (
    <>
      <div className="flex items-start justify-center pt-px">
        <CountryFlag countryCode={countryCode ?? "CA"} />
      </div>
      <div className="flex flex-col">
        {businessName ? (
          <>
            <span className="text-xs font-bold text-content">
              {businessName}
            </span>
            <T size="xs" color="secondary">
              {label}
            </T>
          </>
        ) : (
          <span className="text-xs font-bold text-content">{label}</span>
        )}
      </div>
    </>
  );
}

export function ShipmentDetailsCell({
  pickupName,
  pickupLabel,
  pickupCountry,
  deliveryName,
  deliveryLabel,
  deliveryCountry,
  commodity,
  summary,
}: ShipmentDetailsCellProps) {
  return (
    <div className="max-w-[280px]">
      <div className="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1">
        <LocationLine_
          countryCode={pickupCountry}
          businessName={pickupName}
          label={pickupLabel}
        />
        <LocationLine_
          countryCode={deliveryCountry}
          businessName={deliveryName}
          label={deliveryLabel}
        />
        <div className="mt-2 flex items-start justify-center pt-px">
          <Icon icon={PackageIcon} size="md" />
        </div>
        <div className="mt-2">
          <div className="flex flex-col">
            <Tooltip content={commodity}>
              <span className="block max-w-[230px] truncate text-xs font-bold text-content">
                {commodity}
              </span>
            </Tooltip>
            <T size="xs" color="secondary">
              {summary}
            </T>
          </div>
        </div>
      </div>
    </div>
  );
}
